import { DateField, Select, TextField, Textarea, ToggleField } from '~/components/form';
import { Alert, CardSection } from '~/components/ui';
import type { Scalar } from '~/components/pages/mls-agreements/types';

export type DraftValue = Scalar | boolean;

export interface FieldSpec<D> {
  key: keyof D & string;
  label: string;
  kind?: 'text' | 'number' | 'date' | 'select' | 'textarea' | 'toggle';
  options?: { value: string; label: string }[];
  helper?: string;
  required?: boolean;
  /** Fixed by the link that opened the page (a new membership from its MLS or customer). */
  locked?: boolean;
  /** What "on" means, beside a toggle. */
  text?: string;
  /** Spans the whole row of the section grid. */
  wide?: boolean;
  show?: (draft: D) => boolean;
}

export interface SectionSpec<D> {
  title: string;
  columns?: 1 | 2 | 3;
  fields: FieldSpec<D>[];
}

const COLUMNS = {
  1: 'grid-cols-1',
  2: 'grid-cols-1 sm:grid-cols-2',
  3: 'grid-cols-1 sm:grid-cols-3',
};

const kebab = (key: string) => key.replace(/[A-Z]/g, (c) => `-${c.toLowerCase()}`);

/** The DOM id (and test id) of a field: `mls-fee-amount` for prefix `mls`, key `feeAmount`. */
export const fieldId = (prefix: string, key: string) => `${prefix}-${kebab(key)}`;

interface RecordSectionsProps<D extends Record<string, DraftValue>> {
  prefix: string;
  sections: SectionSpec<D>[];
  draft: D;
  patch: (changes: Partial<D>) => void;
  errors: Record<string, string>;
  touch: (field: string) => void;
}

/** One record's fields as `CardSection`s of inputs. */
export function RecordSections<D extends Record<string, DraftValue>>({
  prefix,
  sections,
  draft,
  patch,
  errors,
  touch,
}: RecordSectionsProps<D>) {
  const set = (key: string, value: DraftValue) => patch({ [key]: value } as Partial<D>);

  const input = (field: FieldSpec<D>) => {
    const id = fieldId(prefix, field.key);
    const value = draft[field.key];
    const common = { id, label: field.label, required: field.required, helperText: field.helper };
    switch (field.kind ?? 'text') {
      case 'toggle':
        return (
          <ToggleField
            id={id}
            testId={id}
            label={field.label}
            text={field.text ?? field.label}
            checked={value === true}
            onChange={(checked) => set(field.key, checked)}
          />
        );
      case 'select':
        return (
          <Select
            {...common}
            options={field.options ?? []}
            value={String(value)}
            onChange={(v) => set(field.key, v)}
          />
        );
      case 'date':
        return (
          <DateField
            {...common}
            value={String(value)}
            onChange={(v) => set(field.key, v)}
            error={errors[field.key]}
          />
        );
      case 'textarea':
        return (
          <Textarea
            {...common}
            testId={id}
            value={String(value)}
            onChange={(v) => set(field.key, v)}
            onBlur={() => touch(field.key)}
            error={errors[field.key]}
          />
        );
      default:
        return (
          <TextField
            {...common}
            testId={id}
            type={field.kind === 'number' ? 'number' : 'text'}
            disabled={field.locked}
            value={String(value)}
            onChange={(v) => set(field.key, v)}
            onBlur={() => touch(field.key)}
            error={errors[field.key]}
          />
        );
    }
  };

  return (
    <>
      {sections.map((section) => (
        <CardSection key={section.title} title={section.title}>
          <div className={`grid gap-4 ${COLUMNS[section.columns ?? 2]}`}>
            {section.fields
              .filter((field) => field.show?.(draft) ?? true)
              .map((field) => (
                <div key={field.key} className={field.wide ? 'sm:col-span-full' : undefined}>
                  {input(field)}
                </div>
              ))}
          </div>
        </CardSection>
      ))}
    </>
  );
}

export const SaveError = ({ message }: { message: string | null }) =>
  message ? (
    <Alert variant="error">
      <p className="text-sm text-gray-800 dark:text-white/90">{message}</p>
    </Alert>
  ) : null;
